'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { MemberRole, hasPermission, canAccessBilling, canManageBilling, canManageTeam } from '@/lib/roles';

interface MembershipData {
  role: MemberRole;
  orgId: string;
  orgName: string;
  orgSlug: string;
}

interface UseUserRoleReturn {
  role: MemberRole | null;
  membership: MembershipData | null;
  loading: boolean;
  error: string | null;
  // Permission helpers
  canViewBilling: boolean;
  canManageBilling: boolean;
  canManageTeam: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
}

export function useUserRole(): UseUserRoleReturn {
  const { data: session, status } = useSession();
  const [membership, setMembership] = useState<MembershipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMembership() {
      if (status === 'loading') return;
      
      if (status === 'unauthenticated' || !session?.user) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch('/api/user/membership');
        
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to fetch membership');
        }

        const data = await res.json();
        setMembership(data.membership);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch membership');
        setMembership(null);
      } finally {
        setLoading(false);
      }
    }

    fetchMembership();
  }, [session, status]);

  const role = membership?.role ?? null;

  return {
    role,
    membership,
    loading: status === 'loading' || loading,
    error,
    // Permission helpers
    canViewBilling: role ? canAccessBilling(role) : false,
    canManageBilling: role ? canManageBilling(role) : false,
    canManageTeam: role ? canManageTeam(role) : false,
    isOwner: role === 'owner',
    isAdmin: role === 'admin',
    isMember: role === 'member',
  };
}

export default useUserRole;
